import React from 'react';
import { observer } from 'mobx-react-lite';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import MessagePropType from '../definitions/MessagePropType';
import Colours from '../definitions/Colours';
import Sizes from '../definitions/Sizes';
import ElementAttributes from '../definitions/ElementAttributes';
import withFieldset from '../functions/withFieldset';
import useCalculatorStepState from '../hooks/useCalculatorStepState';
import useCalculator from '../hooks/useCalculator';
import scrollToCalculatorStep from '../functions/scrollToCalculatorStep';
import Paragraph from './Paragraph';
import PrimaryButton from './PrimaryButton';
import AnimatedFadeContainer from './AnimatedFadeContainer';


function CalculatorStepPanel({ 
    title, 
    ctaText, 
    ctaDataName,
    stepNumber,

    onCheckComplete,
    hasCompleteButton,
    onConfirmComplete,
    
    isVisible,
    includeInnerPadding,

    children, 
    ...otherProps 
}) {
    const calculator = useCalculator();
    const calculatorStepState = useCalculatorStepState({
        number: stepNumber,
        hasCompleteButton,
        onCheckComplete,
    });

    function handleClickComplete() {
        if (!calculatorStepState.canConfirmComplete) {
            return; // do nothing; if there are validation errors, they will be shown.
        }
        
        calculatorStepState.confirmComplete();
        
        // Requirement: Scroll to the next step when panel
        //  is complete so its in view
        const nextStep = calculator.getNextStep(stepNumber);
        nextStep && scrollToCalculatorStep(nextStep.number);

        onConfirmComplete?.(stepNumber);
    }

    return (
        <StyledAnimatedFadeContainer
            hasPadding={includeInnerPadding}
            isVisible={isVisible}
            {...{ [ElementAttributes.CALCULATOR_STEP_KEY]: stepNumber }}
            {...otherProps}
        >
            <If condition={title}>
                <Paragraph
                    className="title"
                    message={title}
                    weight="medium"
                />
            </If>

            {typeof children === 'function'
                ? children({
                    confirmComplete: handleClickComplete,
                })
                : children
            }

            <If condition={hasCompleteButton}>
                <PrimaryButton
                    className="ctaButton"
                    message={ctaText}
                    data-name={ctaDataName}
                    disabled={!calculatorStepState.canConfirmComplete}
                    aria-disabled={!calculatorStepState.canConfirmComplete}
                    onClick={handleClickComplete}
                />
            </If>
        </StyledAnimatedFadeContainer>
    );
}

CalculatorStepPanel.propTypes = {
    title: MessagePropType,
    stepNumber: PropTypes.number.isRequired,
    ctaText: MessagePropType,
    ctaDataName: PropTypes.string,
    
    hasCompleteButton: PropTypes.bool,
    onCheckComplete: PropTypes.func,
    onConfirmComplete: PropTypes.func,
    
    isVisible: PropTypes.bool,
    includeInnerPadding: PropTypes.bool,

    children: PropTypes.oneOfType([
        PropTypes.node,
        PropTypes.elementType, // render prop
    ]).isRequired,
};

CalculatorStepPanel.defaultProps = {
    title: undefined,
    ctaText: undefined,
    ctaDataName: undefined,
    
    hasCompleteButton: undefined,
    onCheckComplete: undefined,
    onConfirmComplete: undefined,
    
    isVisible: false,
    includeInnerPadding: true,
};

const StyledAnimatedFadeContainer = styled(AnimatedFadeContainer)`
    display: flex;
    flex-direction: column;

    background-color: ${Colours.COCONUT};
    border-radius: 0.375rem;
    box-shadow: 0 2px 11px 0 ${Colours.STONE};

    padding: ${({ hasPadding }) => hasPadding ? `${Sizes.SPACING.TWO} 5% 0` : '0' };
    margin: 0 0 ${Sizes.SPACING.HALF} 0;

    position: relative;

    > .title {
        margin: 0 auto ${Sizes.SPACING.TWO}; 
        color: ${Colours.BLUEBERRY_DARK};
    }

    > .ctaButton {
        display: block;
        max-width: 350px;
        margin: ${Sizes.SPACING.TWO} auto ${Sizes.SPACING.THREE};
    }
`;

export default withFieldset(observer(CalculatorStepPanel));